import { useState } from 'react';
import type {
  CardContent,
  MedScreenContent,
  PracticeContent,
  SafetyScreeningContent,
  ScreenContent,
} from '@lma/shared';
import type { MeasureResult, Resolved } from './api';
import {
  CautionBlock,
  ChoiceButton,
  DashList,
  Highlight,
  LegalNote,
  MeasureResultBlock,
  Overline,
  PrimaryButton,
  ProgressDots,
  Scale,
  Secondary,
  SecondaryButton,
  StepsList,
  StopBlock,
  TertiaryButton,
  VideoBlock,
} from './components';

/**
 * Экраны пути.
 *
 * Ни одной строки содержания здесь нет: заголовки, тексты, признаки, шаги проб
 * и подписи кнопок приходят параметрами из базы. Компонент знает тип экрана,
 * но не знает, что на нём написано.
 */

/** Подписи, общие для нескольких экранов. Приходят из `screen:COMMON`. */
export type Labels = Record<string, string>;

const label = (labels: Labels, key: string, fallback: string) => labels[key] ?? fallback;

// ─── W-01 Приветствие ───────────────────────────────────────

export function Welcome({
  screen,
  onStart,
  onHowItWorks,
}: {
  screen: ScreenContent;
  onStart: () => void;
  onHowItWorks: () => void;
}) {
  return (
    <main className="screen">
      <VideoBlock
        screen="W-01"
        url={screen.videoUrl}
        caption={screen.videoCaption}
        posterUrl={screen.videoPosterUrl}
        aspect={screen.videoAspect}
      />
      <div className="stack">
        <h1 className="welcome">{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}
        {screen.highlight ? <Highlight>{screen.highlight}</Highlight> : null}
        {screen.legalNote ? <LegalNote>{screen.legalNote}</LegalNote> : null}
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={onStart}>
          {label(screen.buttons, 'get_route', 'Получить мой маршрут')}
        </PrimaryButton>
        <TertiaryButton onClick={onHowItWorks}>
          {label(screen.buttons, 'how_it_works', 'Как это работает')}
        </TertiaryButton>
      </div>
    </main>
  );
}

// ─── W-01A Как это работает ─────────────────────────────────

export function HowItWorks({
  screen,
  onStart,
  onBack,
}: {
  screen: ScreenContent;
  onStart: () => void;
  onBack: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
        {screen.body.slice(0, 1).map((p) => (
          <p key={p}>{p}</p>
        ))}
        <DashList items={screen.bullets} />
        {screen.body.slice(1).map((p) => (
          <p key={p}>{p}</p>
        ))}
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={onStart}>
          {label(screen.buttons, 'get_route', 'Получить мой маршрут')}
        </PrimaryButton>
        <TertiaryButton onClick={onBack}>{label(screen.buttons, 'back', 'Назад')}</TertiaryButton>
      </div>
    </main>
  );
}

// ─── W-02 Границы сервиса ───────────────────────────────────

export function Boundaries({
  screen,
  onContinue,
  onBack,
}: {
  screen: ScreenContent;
  onContinue: () => void;
  onBack: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={onContinue}>
          {label(screen.buttons, 'understood', 'Понятно, продолжить')}
        </PrimaryButton>
        <TertiaryButton onClick={onBack}>{label(screen.buttons, 'back', 'Назад')}</TertiaryButton>
      </div>
    </main>
  );
}

// ─── W-03 Скрининг красных флагов ───────────────────────────

export function Screening({
  content,
  onAnswer,
}: {
  content: SafetyScreeningContent;
  onAnswer: (hasFlags: boolean) => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <Overline>Проверка безопасности</Overline>
        <h1>{content.title}</h1>
        <StopBlock items={content.flags} />
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={() => onAnswer(false)}>Ничего из этого нет</PrimaryButton>
        <SecondaryButton onClick={() => onAnswer(true)}>Есть или я не уверена</SecondaryButton>
        <LegalNote>{content.legalNote}</LegalNote>
      </div>
    </main>
  );
}

// ─── W-03A Медицинский маршрут ──────────────────────────────

export function MedicalRoute({
  screen,
  onCleared,
  onRemind,
}: {
  screen: ScreenContent;
  onCleared: () => void;
  onRemind: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}
      </div>
      <div className="stack actions">
        {/* Единственный способ снять стоп-статус. Дата сохраняется. */}
        <SecondaryButton onClick={onCleared}>
          {label(screen.buttons, 'passed_medical_check', 'Я прошла медицинскую оценку')}
        </SecondaryButton>
        <SecondaryButton onClick={onRemind}>
          {label(screen.buttons, 'remind_in_7_days', 'Напомнить через 7 дней')}
        </SecondaryButton>
      </div>
    </main>
  );
}

// ─── S-01 Выбор запроса ─────────────────────────────────────

export function SelectRequest({
  screen,
  cards,
  onSelect,
}: {
  screen: ScreenContent;
  cards: Resolved<CardContent>[];
  onSelect: (key: string) => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <Secondary key={p}>{p}</Secondary>
        ))}
      </div>
      <div className="stack choices">
        {cards.map((card) => (
          <ChoiceButton key={card.key} onClick={() => onSelect(card.key)}>
            {card.data.title}
          </ChoiceButton>
        ))}
      </div>
    </main>
  );
}

// ─── C Карточка запроса ─────────────────────────────────────

export function Card({
  card,
  labels,
  onTry,
  onMed,
  onOther,
}: {
  card: CardContent;
  labels: Labels;
  onTry: () => void;
  onMed: () => void;
  onOther: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <Overline>Ваша точка старта</Overline>
        <h1>{card.title}</h1>
        {card.bullets.length > 0 ? (
          <>
            <h2>{label(labels, 'recognition_title', 'Если большая часть похожа на вас')}</h2>
            <DashList items={card.bullets} />
          </>
        ) : null}
        {card.body.map((p) => (
          <p key={p}>{p}</p>
        ))}
        <CautionBlock title={label(labels, 'caution_title', 'Чего из этого нельзя заключить')}>
          {card.caution}
        </CautionBlock>
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={onTry}>
          {label(labels, 'try_now', 'Попробовать сейчас')}
        </PrimaryButton>
        <SecondaryButton onClick={onMed}>
          {label(labels, 'when_doctor_needed', 'Когда нужен врач')}
        </SecondaryButton>
        <TertiaryButton onClick={onOther}>
          {label(labels, 'choose_another', 'Выбрать другой запрос')}
        </TertiaryButton>
      </div>
    </main>
  );
}

// ─── C-MED Медицинский подэкран ─────────────────────────────

export function CardMedical({
  med,
  labels,
  onBack,
  onMedicalRoute,
}: {
  med: MedScreenContent;
  labels: Labels;
  onBack: () => void;
  onMedicalRoute: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{med.title}</h1>
        <StopBlock title={label(labels, 'urgent_title', 'Срочно')} items={med.urgent} />
        <h2>{label(labels, 'plan_title', 'Планово')}</h2>
        <p>{med.plan}</p>
        {med.note ? <LegalNote>{med.note}</LegalNote> : null}
      </div>
      <div className="stack actions">
        <SecondaryButton onClick={onBack}>
          {label(labels, 'back_to_card', 'Вернуться к карточке')}
        </SecondaryButton>
        <SecondaryButton onClick={onMedicalRoute}>
          {label(labels, 'go_medical_route', 'Перейти к медицинскому маршруту')}
        </SecondaryButton>
      </div>
    </main>
  );
}

// ─── M-BEFORE / M-AFTER Замеры ──────────────────────────────

/**
 * Замер по шкале.
 *
 * Только шкала и ничего больше. Запись словами переехала на экран
 * результата: писать наблюдения имеет смысл, когда видишь, что изменилось,
 * а не до того. Два поля для заметок на соседних экранах женщина просто
 * не заполнит — заполнит одно, и неизвестно какое.
 */
export function Measure({
  question,
  note,
  step,
  labels,
  buttonLabel,
  onSubmit,
}: {
  question: string;
  note?: string | null;
  step: 1 | 3;
  labels: Labels;
  buttonLabel: string;
  onSubmit: (value: number) => void;
}) {
  const [value, setValue] = useState<number | null>(null);

  return (
    <main className="screen">
      <div className="stack">
        <ProgressDots current={step} total={3} label={`Шаг ${step} из 3`} />
        <h1>{question}</h1>
        {step === 1 ? <Secondary>Не задумывайтесь долго. Первое ощущение точнее.</Secondary> : null}
        {note ? <Secondary>{note}</Secondary> : null}
        <Scale
          value={value}
          onChange={setValue}
          lowLabel={label(labels, 'scale_low', 'нет совсем')}
          highLabel={label(labels, 'scale_high', 'максимально выражено')}
        />

      </div>
      <div className="stack actions">
        {/* Кнопка неактивна, пока не сделан выбор по шкале. */}
        <PrimaryButton
          onClick={() => value !== null && onSubmit(value)}
          disabled={value === null}
        >
          {buttonLabel}
        </PrimaryButton>
      </div>
    </main>
  );
}

// ─── P Проба ────────────────────────────────────────────────

export function Probe({
  practice,
  labels,
  onDone,
  onWorse,
}: {
  practice: PracticeContent;
  labels: Labels;
  onDone: () => void;
  onWorse: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <ProgressDots current={2} total={3} label="Шаг 2 из 3" />
        <h1>{practice.title}</h1>
        <VideoBlock
          screen="P"
          url={practice.videoUrl}
          caption={practice.videoCaption}
          posterUrl={practice.videoPosterUrl}
          aspect={practice.videoAspect}
        />

        {/* Исходное положение — это нулевой шаг: без него остальные не работают. */}
        <div className="probe-setup">
          <span className="probe-duration">{durationLabel(practice.durationSec)}</span>
          <p>{practice.startPosition}</p>
        </div>

        <StepsList steps={practice.steps} />

        {/*
          Секрет практики. У Елены это отдельный блок в каждой пробе, и он там
          не для красоты: без него женщина делает движение, но не делает
          практику — давит сильнее, чем нужно, и напрягает то, что должно
          расслабиться.
        */}
        {practice.secret ? (
          <div className="probe-secret">
            <span className="probe-secret-label">Секрет практики</span>
            <p>{practice.secret}</p>
          </div>
        ) : null}

        {practice.variantEasier ? (
          <div className="probe-easier">
            <span className="probe-easier-label">Если сейчас тяжело</span>
            <p>{practice.variantEasier}</p>
          </div>
        ) : null}

        <CautionBlock>{practice.caution}</CautionBlock>
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={onDone}>{label(labels, 'done', 'Я сделала')}</PrimaryButton>
        <SecondaryButton onClick={onWorse}>
          {label(labels, 'got_worse', 'Стало хуже')}
        </SecondaryButton>
      </div>
    </main>
  );
}

/** «Полторы минуты» понятнее, чем «90 секунд». */
function durationLabel(seconds: number): string {
  if (seconds < 60) return `${seconds} секунд`;
  if (seconds === 60) return 'минута';
  if (seconds === 90) return 'полторы минуты';
  const minutes = Math.round(seconds / 60);
  if (minutes === 2) return 'две минуты';
  if (minutes === 3) return 'три минуты';
  return `${minutes} минут`;
}

// ─── C-Q Дополнительный вопрос ──────────────────────────────

export function ExtraQuestion({
  question,
  options,
  onAnswer,
}: {
  question: string;
  options: string[];
  onAnswer: (choice: string) => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{question}</h1>
      </div>
      <div className="stack choices">
        {options.map((option) => (
          <ChoiceButton key={option} onClick={() => onAnswer(option)}>
            {option}
          </ChoiceButton>
        ))}
      </div>
    </main>
  );
}

// ─── R Результат и маршрут ──────────────────────────────────

export function Result({
  card,
  result,
  labels,
  onSaveNote,
  onPrimary,
  onSecondary,
  onOther,
}: {
  card: CardContent;
  result: MeasureResult;
  labels: Labels;
  onSaveNote: (input: { text: string; cycleDay: number | null }) => Promise<void>;
  onPrimary: () => void;
  onSecondary: () => void;
  onOther: () => void;
}) {
  const [text, setText] = useState('');
  const [cycleDay, setCycleDay] = useState('');
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const day = cycleDay === '' ? null : Number(cycleDay);
  const hasSomething = text.trim().length > 0 || day !== null;

  const save = () => {
    if (!hasSomething || saving) return;
    setSaving(true);
    void onSaveNote({ text: text.trim(), cycleDay: day })
      .then(() => setSaved(true))
      .finally(() => setSaving(false));
  };

  return (
    <main className="screen">
      <div className="stack">
        <Overline>Ваш результат</Overline>
        <MeasureResultBlock
          result={result}
          note={label(
            labels,
            'result_note',
            'Изменение не устанавливает причину. Оно показывает, что в вашем запросе есть компонент, который способен меняться.',
          )}
        />

        {card.blind ? (
          <>
            <h2>{label(labels, 'blind_title', 'Что сейчас не нужно делать вслепую')}</h2>
            <p>{card.blind}</p>
          </>
        ) : null}

        {/*
          Мои наблюдения.

          Шкала показывает, насколько изменилось, и молчит о том, что
          именно: в шее могло стать легче, а челюсть остаться зажатой.
          День цикла здесь же и не случайно — отёчность заметно зависит
          от фазы, и без него запись «стало хуже» не отличить от обычного
          предменструального отёка.

          Поля необязательные, и кнопки дальше от них не зависят: молчание
          тоже ответ.
        */}
        <section className="journal">
          <h2>{label(labels, 'journal_title', 'Мои наблюдения')}</h2>
          <Secondary>
            {label(
              labels,
              'journal_hint',
              'Эти записи останутся у вас — к ним можно вернуться через месяц.',
            )}
          </Secondary>

          <label className="ask-field">
            <span>{label(labels, 'journal_text_label', 'Что вы заметили')}</span>
            <textarea
              rows={4}
              value={text}
              maxLength={4000}
              onChange={(e) => {
                setText(e.target.value);
                setSaved(false);
              }}
              placeholder={label(
                labels,
                'journal_text_hint',
                'Например: в шее стало легче, а челюсть по-прежнему сжата',
              )}
            />
          </label>

          <label className="ask-field cycle">
            <span>{label(labels, 'journal_cycle_label', 'День цикла')}</span>
            <input
              inputMode="numeric"
              value={cycleDay}
              onChange={(e) => {
                setCycleDay(e.target.value.replace(/\D/g, '').slice(0, 2));
                setSaved(false);
              }}
              placeholder="—"
            />
            <Secondary>
              {label(
                labels,
                'journal_cycle_hint',
                'Если цикла нет или вы его не отслеживаете — оставьте пустым.',
              )}
            </Secondary>
          </label>

          <SecondaryButton onClick={save} disabled={!hasSomething || saving || saved}>
            {saved
              ? (label(labels, 'journal_saved', 'Записано'))
              : saving
                ? 'Сохраняем…'
                : (label(labels, 'journal_save', 'Сохранить наблюдение'))}
          </SecondaryButton>
        </section>

        <h2>{label(labels, 'route_title', 'Ваш следующий шаг')}</h2>
        <p>{card.route}</p>
        {card.resultNote ? <Secondary>{card.resultNote}</Secondary> : null}
      </div>

      <div className="stack actions">
        <PrimaryButton onClick={onPrimary}>{card.primary.label}</PrimaryButton>
        {/* У карточки «всё сразу» второй кнопки нет намеренно. */}
        {card.secondary ? (
          <SecondaryButton onClick={onSecondary}>{card.secondary.label}</SecondaryButton>
        ) : null}
        <TertiaryButton onClick={onOther}>
          {label(labels, 'choose_another', 'Выбрать другой запрос')}
        </TertiaryButton>
        <LegalNote>
          {label(
            labels,
            'morning_note',
            'Завтра утром мы спросим, как отреагировало лицо. Ваш результат сохранён.',
          )}
        </LegalNote>
      </div>
    </main>
  );
}

// ─── WORSE Стало хуже ───────────────────────────────────────

export function Worse({
  screen,
  onMedicalRoute,
  onRest,
}: {
  screen: ScreenContent;
  onMedicalRoute: () => void;
  onRest: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
        {screen.body.map((p) => (
          <p key={p}>{p}</p>
        ))}
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={onMedicalRoute}>
          {label(screen.buttons, 'go_medical_route', 'Перейти к медицинскому маршруту')}
        </PrimaryButton>
        <SecondaryButton onClick={onRest}>
          {label(screen.buttons, 'just_need_rest', 'Мне просто нужно отдохнуть')}
        </SecondaryButton>
      </div>
    </main>
  );
}

// ─── RETURN Возвращение ─────────────────────────────────────

export function Return({
  screen,
  onResult,
  onOther,
}: {
  screen: ScreenContent;
  onResult: () => void;
  onOther: () => void;
}) {
  return (
    <main className="screen">
      <div className="stack">
        <h1>{screen.title}</h1>
      </div>
      <div className="stack actions">
        <PrimaryButton onClick={onResult}>
          {label(screen.buttons, 'open_my_result', 'Открыть мой результат')}
        </PrimaryButton>
        <SecondaryButton onClick={onOther}>
          {label(screen.buttons, 'choose_another', 'Выбрать другой запрос')}
        </SecondaryButton>
      </div>
    </main>
  );
}

/** Заглушка продуктовых экранов: они приходят на этапе 3. */
export function Placeholder({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <main className="screen">
      <div className="stack">
        <Overline>Этап 3</Overline>
        <h1>{title}</h1>
        <Secondary>
          Этот экран собирается следующим. Ваш выбор уже записан — он попадёт в воронку.
        </Secondary>
      </div>
      <div className="stack actions">
        <SecondaryButton onClick={onBack}>Вернуться</SecondaryButton>
      </div>
    </main>
  );
}
